import { inject, Injectable } from '@angular/core';
import {
  ChartDatasetConfig,
  ChartDataService,
  ExtendedChartDataset,
} from '@app/core/services/charts/chart-data.service';
import { ChartData, ChartOptions, Plugin, TooltipOptions } from 'chart.js';
import { _, TranslateService } from '@ngx-translate/core';
import { splitTextByLines } from '@app/shared/utils/split-label-by-words';
import { roundToTwoDecimals } from '@app/shared/utils/math';
import { ColorManagementService } from '@app/core/services/colors/color-management.service';
import { _DeepPartialObject } from 'node_modules/chart.js/dist/types/utils';
import { ILocalizationKey } from '@app/core/interfaces/localization';
import { ChartColorType } from '@app/core/enums/chart-color-type.enum';
import { ChartDataSoundStatus } from '@app/core/enums/chart-data-sound-status.enum';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { FloatingPopupBarChartService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-bar-chart.service';
import { ICombinedMinNoiseChartData } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-min-noise/interfaces/combined-min-noise-chart-data';
import { createBarBottomCirclesPlugin } from '@app/core/services/charts/plugins/bar-bottom-circles.plugin';
import { ChartThemeService } from '@app/core/services/charts/chart-theme.service';

@Injectable()
export class FpLoggerNoiseMinNoiseChartService {
  protected floatingPopupBarChartService = inject(FloatingPopupBarChartService);
  protected chartDataService = inject(ChartDataService);
  protected translate: TranslateService = inject(TranslateService);
  protected colorManagementService = inject(ColorManagementService);
  protected formattingDataService = inject(FormattingDataService);
  protected chartThemeService = inject(ChartThemeService);

  private noDataLocalizationKey: ILocalizationKey = _(`@@shared-no-data-value-placeholder`);

  public getBottomCirclesPlugin(): Plugin {
    return createBarBottomCirclesPlugin<ICombinedMinNoiseChartData>({
      condition: item => item?.status === ChartDataSoundStatus.NoSound,
      color: this.chartThemeService.getColors().errorLabelColor,
    });
  }

  public generateOptions(): ChartOptions {
    const tooltipOptions: _DeepPartialObject<TooltipOptions> = this.getTooltipOptions();

    return this.floatingPopupBarChartService.generateOptions({
      chartTranslation: {
        yLabel: _('@@chart-noise-level__db'),
        xLabel: _('@@shared-date'),
        titleText: null,
      },
      beginAtZero: true,
      customTooltipConfig: tooltipOptions,
    });
  }

  public generateMinimizedOptions(): ChartOptions {
    const tooltipOptions: _DeepPartialObject<TooltipOptions> = this.getTooltipOptions();

    return this.floatingPopupBarChartService.generateOptions({
      chartTranslation: {
        yLabel: _('@@chart-noise-level__db'),
        xLabel: _('@@shared-date'),
        titleText: null,
      },
      beginAtZero: true,
      customTooltipConfig: tooltipOptions,
      interactionsEnabled: false,
    });
  }

  private dataConverter(data: ICombinedMinNoiseChartData): number | null {
    return roundToTwoDecimals(data.lowestNoiseDecibel) ?? null;
  }

  private generateDatasetConfig(): ChartDatasetConfig<ICombinedMinNoiseChartData>[] {
    const getMainColor = (): tinycolor.Instance =>
      this.colorManagementService.getChartDefaultColor(ChartColorType.Primary);

    return [
      {
        label: '',
        tooltipLabel: '',
        type: 'bar',
        dataValue: (data: ICombinedMinNoiseChartData) => this.dataConverter(data),
        backgroundColor: () =>
          this.colorManagementService.getColorInstanceWithOpacity(getMainColor(), 0.6).toRgbString(),
        hoverBackgroundColor: () => getMainColor().toRgbString(),
        borderWidth: 0,
        barPercentage: 0.85,
        categoryPercentage: 1.0,
        minBarLength: 4,
      },
    ];
  }

  public getDataForChart(data: ICombinedMinNoiseChartData[]): ChartData {
    const datasetConfigs: ChartDatasetConfig<ICombinedMinNoiseChartData>[] = this.generateDatasetConfig();

    const labels = data.map(el => this.formattingDataService.formatDate(el.date));

    return {
      labels: labels.length ? labels : [this.translate.instant(this.noDataLocalizationKey)],
      datasets: this.chartDataService.generateDatasets<ICombinedMinNoiseChartData>([...data], datasetConfigs),
    };
  }

  public getMinimizedDataForChart(data: ICombinedMinNoiseChartData[]): ChartData {
    const datasetConfigs: ChartDatasetConfig<ICombinedMinNoiseChartData>[] = this.generateDatasetConfig();

    const labels = data.map(el => this.formattingDataService.formatDate(el.date));

    return {
      labels: labels.length ? labels : [this.translate.instant(this.noDataLocalizationKey)],
      datasets: this.chartDataService.generateDatasets<ICombinedMinNoiseChartData>([...data], datasetConfigs),
    };
  }

  private getTooltipOptions(): _DeepPartialObject<TooltipOptions> {
    return {
      callbacks: {
        title: () => {
          const text = this.translate.instant(_('@@shared-min-noise__text'));

          return splitTextByLines(text, 40);
        },
        beforeLabel: tooltipItem => {
          const dataset: ExtendedChartDataset<ICombinedMinNoiseChartData> = tooltipItem.dataset;

          const currentData: ICombinedMinNoiseChartData | null = dataset.meta
            ? dataset.meta[tooltipItem.dataIndex]
            : null;

          const isNoSound = !!currentData && currentData.status === ChartDataSoundStatus.NoSound;

          return isNoSound ? [this.translate.instant(_('@@shared-no-sound-file'))] : undefined;
        },
        label: tooltipItem => {
          const noDataText: string = this.translate.instant(this.noDataLocalizationKey);

          const dataset: ExtendedChartDataset<ICombinedMinNoiseChartData> = tooltipItem.dataset;

          const currentData: ICombinedMinNoiseChartData | null = dataset.meta
            ? dataset.meta[tooltipItem.dataIndex]
            : null;

          const value = currentData?.lowestNoiseDecibel ?? null;

          const yFormattedValue =
            value !== null ? this.floatingPopupBarChartService.formatChartNumber(value, 0, 2) : null;

          const separator = '   ';

          return this.translate.instant(_('@@chart-tooltip-date-decibel'), {
            I18N_VAR_X_VALUE: tooltipItem.label ?? noDataText,
            I18N_VAR_SEPARATOR: separator,
            I18N_VAR_Y_VALUE: yFormattedValue ?? noDataText,
          });
        },
      },
    };
  }
}
